import { booleanAttribute, Component, HostBinding, HostListener, inject, Input, input, output, signal } from '@angular/core';
import { AcceptService, DropzoneService } from '@ngx-dropzone/cdk';
import { MuiIconFont, MuiIconModule } from '../icon';


@Component({
  selector: 'mui-dropzone',
  standalone: true,
  templateUrl: './mui-dropzone.component.html',
  styleUrl: './mui-dropzone.component.scss',
  imports: [
    MuiIconModule
  ],
  providers: [
    DropzoneService,
    AcceptService
  ],
  /* eslint-disable  @typescript-eslint/naming-convention */
  host: {
    class: 'mui-dropzone',
    role: 'presentation',
    '[class.disabled]': 'disabled()',
    '[class.dragover]': 'dragover()',
    '[attr.aria-labelledby]': 'label() ? labelId : null',
    '[attr.disabled]': 'disabled() ? true : null',
    '[attr.aria-disabled]': 'disabled() ? true : null'
  }
  /* eslint-enable  @typescript-eslint/naming-convention */
})
export class MuiDropzone {

  static uid = 0;

  @Input()
  @HostBinding('id')
  set id(value: string) {
    this._id = value;
  }

  get id(): string {
    return this._id;
  }

  readonly accept = input<string>();
  readonly label = input<string>();
  readonly description = input<string>();
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly hideIcon = input(false, { transform: booleanAttribute });
  readonly dragover = signal(false);
  readonly icon = input<string>('circle-arrow-down');
  readonly iconFont = input<MuiIconFont | string>(MuiIconFont.FONTAWESOME);
  readonly onFileDrop = output<File[]>();

  protected get labelId(): string {
    return `${this.id}-label`;
  }

  private readonly _acceptService = inject(AcceptService);
  private readonly _dropzoneService = inject(DropzoneService);
  private _id = `mui-dropzone-${MuiDropzone.uid++}`;

  @HostListener('dragover', ['$event'])
  protected onDragOver = (event: DragEvent): void => {
    event.preventDefault();
  };

  @HostListener('dragenter', ['$event'])
  protected onDragEnter = async (event: DragEvent): Promise<void> => {
    this.dragover.set(true);

    event.preventDefault();
    if (event.dataTransfer) {
      // Indicate to the Browser that files will be copied
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  @HostListener('dragleave', ['$event'])
  protected onDragLeave = (event: DragEvent): void => {
    event.preventDefault();
    this.dragover.set(false);
  };

  @HostListener('drop', ['$event'])
  protected onDrop = async (event: DragEvent): Promise<void> => {
    this.onDragLeave(event);
    try {
      const files = await this.getAcceptedFilesFromEvent(event);
      this.onFileDrop.emit(files);
    } catch (e) {
      console.warn('Failed to get files from drop event', e);
    }
  };

  /**
   * Extracts the files from the drop event and filters them based on the accept property.
   *
   * @see https://www.w3.org/TR/2010/WD-html-markup-20101019/input.file.html#input.file.attrs.accept
   * @param {DragEvent} event
   * @returns {Promise<File[]>}
   * @private
   */
  private async getAcceptedFilesFromEvent(event: DragEvent): Promise<File[]> {
    const allFiles = await this._dropzoneService.getFiles(event);
    const accept = this.accept() ?? '';
    return accept
           ? allFiles.filter(file => this._acceptService.accepts(file, accept))
           : allFiles;
  }
}
